import { ChainID, Timeframe } from '@ankr.com/chains-list';
import { useMemo } from 'react';

import { ProjectStatsParams } from 'modules/projects/types';
import { SHORT_TIMEFRAME_LIST } from 'modules/chains/constants';
import { StatsTimeframe } from 'modules/stats/types';
import { selectAllPathsByChainId } from 'modules/chains/store/selectors';
import { useAppSelector } from 'store/useAppSelector';
import { useJWTsManager } from 'domains/jwtToken/hooks/useJWTsManager';
import { usePrivateStats } from 'modules/stats/hooks/usePrivateStats';
import { useProjectsStatsParams } from 'modules/projects/hooks/useProjectsStatsParams';
import { useTimeframe } from 'modules/chains/hooks/useTimeframe';

export interface IUseChainProjectsProps {
  chainId: ChainID;
}

export const useChainProjects = ({ chainId }: IUseChainProjectsProps) => {
  const { timeframe, timeframeTabs } = useTimeframe({
    initialTimeframe: Timeframe.Day,
    timeframes: SHORT_TIMEFRAME_LIST,
  });

  const isDayTimeframe = timeframe === Timeframe.Day;

  const {
    isLoaded: projectsLoaded,
    jwts: projects,
    jwtsLoading: projectsLoading,
    shouldShowTokenManager,
  } = useJWTsManager();

  const blockchains = useAppSelector(state =>
    selectAllPathsByChainId(state, chainId),
  );

  const { params: statsParams1h } = usePrivateStats(
    useProjectsStatsParams({
      blockchains,
      timeframe: StatsTimeframe.Hour,
      timeframeShift: 0,
      skipFetching: !projectsLoaded || isDayTimeframe,
    }),
  );

  const { params: statsParams1d } = usePrivateStats(
    useProjectsStatsParams({
      blockchains,
      timeframe: StatsTimeframe.Day,
      timeframeShift: 0,
      skipFetching: !projectsLoaded || !isDayTimeframe,
    }),
  );

  const projectsStatsParams = useMemo(
    (): ProjectStatsParams => ({
      [StatsTimeframe.Hour]: statsParams1h,
      [StatsTimeframe.Day]: statsParams1d,
    }),
    [statsParams1h, statsParams1d],
  );

  return {
    projects,
    projectsLoading,
    projectsStatsParams,
    shouldShowTokenManager,
    timeframe,
    timeframeTabs,
  };
};
